import "chart.js/auto";
import { Line } from "react-chartjs-2";
import { CustomDialog } from '@/components/dialog/v1/CustomDialog';
import { useInflowLinkQuery } from "@/hooks/ReactQuery/InflowAnalyticsReactQuery/InflowAnalyticsReactQuery";
import { ProBadge, ProUpsell } from '../../FdSubscription/proPlan';
import { FgRankBars } from "./FgRankBars";
import { FgUtmTable } from "./FgUtmTable";
import { COLORS, DEVICE_LABELS, IN_APP_LABELS, formatNumber, formatPercent, formatShortDate } from "../utils";

function Tile({ label, value, hint }) {
    return (
        <div className="rounded-xl bg-gray-50 px-3 py-2.5" title={hint}>
            <p className="text-[11px] text-gray-500">{label}</p>
            <p className="mt-0.5 text-lg font-bold">{value}</p>
        </div>
    );
}

function DailyClicks({ daily }) {
    const data = {
        labels: daily.map(d => formatShortDate(d.date)),
        datasets: [{
            label: '클릭',
            data: daily.map(d => d.clicks),
            borderColor: COLORS.clicks,
            backgroundColor: COLORS.clicks,
            borderWidth: 2,
            pointRadius: daily.length > 31 ? 0 : 2.5,
            tension: 0.3,
        }],
    };
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { display: false },
            tooltip: { backgroundColor: '#111827', padding: 10, callbacks: { title: (items) => daily[items[0].dataIndex].date, label: (item) => ` 클릭 ${formatNumber(item.parsed.y)}` } },
        },
        scales: {
            x: { grid: { display: false }, ticks: { color: '#6b7280', maxTicksLimit: 6, maxRotation: 0 } },
            y: { beginAtZero: true, grid: { color: '#f1f1f1' }, ticks: { color: '#6b7280', precision: 0, maxTicksLimit: 4 } },
        },
    };
    return (
        <div className="h-[180px]">
            <Line data={data} options={options} role="img" aria-label="일별 클릭 추이" />
        </div>
    );
}

function Block({ title, children }) {
    return (
        <div>
            <p className="mb-2 text-xs font-semibold text-gray-500">{title}</p>
            {children}
        </div>
    );
}

/** 링크 하나의 클릭 상세 (슈룩 Pro). 무료는 안내만 */
export function FgLinkDetail({ open, onClose, userPageId, link, range, isPro }) {
    const query = useInflowLinkQuery({ userPageId, linkItemId: link?.id, ...range, enabled: open && isPro });
    const data = query?.data;

    return (
        <CustomDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <CustomDialog.CloseButton onClose={onClose} />
            <div className="flex flex-col gap-5 px-5 pb-6 min-[600px]:px-6">
                <div className="min-w-0">
                    <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-500">링크 상세 분석 <ProBadge /></p>
                    <h3 className="mt-1 truncate text-lg font-bold" title={link?.title || ''}>{link?.title}</h3>
                    {link?.linkUrl && <p className="truncate text-xs text-gray-400">{link.linkUrl}</p>}
                </div>

                {!isPro &&
                    <ProUpsell title="링크 상세 분석" description="이 링크를 누른 사람이 어디서 왔는지(채널·앱·캠페인), 어떤 기기였는지, 날짜별로 몇 번 눌렸는지 볼 수 있어요." />
                }

                {isPro && query?.isLoading && <div className="h-[320px] animate-pulse rounded-xl bg-gray-100" aria-busy="true" />}

                {isPro && query?.isError &&
                    <p className="rounded-xl bg-gray-50 p-6 text-center text-sm text-gray-500">
                        {query.error?.response?.data?.message || '링크 상세를 불러오지 못했습니다.'}
                    </p>
                }

                {data &&
                    <>
                        <p className="-mt-3 text-xs text-gray-500">{data.from.replace(/-/g, '.')} ~ {data.to.replace(/-/g, '.')} · 한국 시간 기준</p>
                        <div className="grid grid-cols-3 gap-2">
                            <Tile label="클릭" value={formatNumber(data.summary.clicks)} />
                            <Tile label="유효 클릭" value={formatNumber(data.summary.firstClicks)} hint="같은 날 같은 브라우저의 재클릭 제외" />
                            <Tile label="클릭률" value={formatPercent(data.summary.clickRate)} hint="이 링크 클릭 ÷ 페이지 방문" />
                        </div>
                        <Block title="날짜별 클릭"><DailyClicks daily={data.daily} /></Block>
                        <Block title="유입 채널">
                            <FgRankBars total={data.summary.clicks} rows={data.channels.map(c => ({ key: c.key, label: c.label, value: c.clicks }))} emptyText="이 기간에 클릭이 없습니다." />
                        </Block>
                        <div className="grid grid-cols-1 min-[600px]:grid-cols-3 gap-5">
                            <Block title="기기">
                                <FgRankBars total={data.trackedClicks} rows={data.devices.map(r => ({ key: r.key, label: DEVICE_LABELS[r.key] || r.key || '알 수 없음', value: r.clicks }))} emptyText="수집된 정보가 없습니다." />
                            </Block>
                            <Block title="열린 앱">
                                <FgRankBars total={data.trackedClicks} rows={data.inApps.map(r => ({ key: r.key, label: IN_APP_LABELS[r.key] || r.key || '일반 브라우저', value: r.clicks }))} emptyText="수집된 정보가 없습니다." />
                            </Block>
                            <Block title="캠페인 (?src=)">
                                <FgRankBars total={data.trackedClicks} rows={data.sources.map(r => ({ key: r.key, label: r.key ?? '지정 안 함', value: r.clicks }))} emptyText="수집된 정보가 없습니다." />
                            </Block>
                        </div>
                        <Block title="캠페인 (UTM)">
                            <FgUtmTable rows={data.utm} emptyText="UTM 이 붙은 주소로 들어와 이 링크를 누른 기록이 없습니다." />
                        </Block>
                        <p className="text-[11px] text-gray-400">검색 봇·링크 미리보기로 판별된 클릭은 제외합니다. 기기·앱·캠페인은 수집을 시작한 뒤의 클릭 {formatNumber(data.trackedClicks)}건 기준이에요.</p>
                    </>
                }
            </div>
        </CustomDialog>
    );
}
